import { acceptHMRUpdate, defineStore } from 'pinia';
import { useRoute, useRouter } from 'nuxt/app';
import type { LocationQuery, RouteQueryAndHash } from 'vue-router';
import {
  QUERY_KEY_NAME_PAGE,
  QUERY_KEY_NAME_SORT,
  QUERY_KEY_NAME_SORT_DIR,
  QUERY_KEY_SRSLTID,
  ROUTE_ONLY_QUERY_KEYS
} from './constants';
import type {
  ProductFiltersModel,
  ReadonlyProductFiltersModel,
  ResponseModel,
  SortDirection
} from './model';
import { createFiltersFromQuery } from './helper/createFiltersFromQuery';
import {
  mergeAggregationCodes,
  pruneFiltersToKnownCodes,
  ALWAYS_ALLOWED_FILTER_KEYS
} from './helper/knownFilterCodes';
import { CATEGORY_ID } from '~/utils/shop/constants';
import { withReset } from '~/utils/pinia/withReset';
import type { ProductsQueryModel } from '~~/server/api/magento/products.get';
import { isDefined } from '~/utils/guards/isDefined';
import { ensureArray } from '~/utils/ensureArray';
import type {
  CommonProductDetailFragment,
  CommonProductFragment,
  ProductAggregationFragment
} from '~~/lib/Shop/generated/schema';
import { CurrencyEnum } from '~~/lib/Shop/generated/schema';
import { mergeFilters } from '~/utils/shop/mergeFilters';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import { clientOnlyRef } from '~/utils/clientOnlyRef';
import type { IProductList } from '~~/lib/ContentfulService';
import { schedulePrefetchMagentoProductListAttributeMetadata } from '~/composables/useMagentoProductListAttributeMetadata';
import { readStoredProductListView } from './helper/resolveProductListView';

const EXCLUDED_KEYS = [
  QUERY_KEY_NAME_SORT,
  QUERY_KEY_NAME_SORT_DIR,
  QUERY_KEY_NAME_PAGE,
  QUERY_KEY_SRSLTID
];

const SORT_DIRECTION_MAP: Record<string, SortDirection> = {
  asc: 'asc',
  desc: 'desc'
};

const DEFAULT_FILTERS: ReadonlyProductFiltersModel = Object.freeze({
  [QUERY_KEY_NAME_SORT_DIR]: Object.freeze(['asc']),
  [QUERY_KEY_NAME_PAGE]: Object.freeze(['1'])
});

// Unmatchable SKU used to force an empty Magento result when the Lighting
// Advisor returned no recommended products.
const LIGHTING_NO_MATCH_SKU = '__lighting_no_match__';
const CAMERA_NO_MATCH_SKU = '__camera_no_match__';

// Fallback page size for Lighting Advisor virtual pagination until the real
// grid page size is known from a regular (non-lighting) response.
const DEFAULT_PAGE_SIZE = 21;

// Debounce window for facet filter changes so users can tick multiple
// checkboxes before the route update triggers a product reload.
const FILTER_APPLY_DEBOUNCE_MS = 400;

export const PRODUCT_FILTER_STORE_INJECT_KEY = Symbol('product-filter-store');

type ProductFilterStoreDefinition = ReturnType<typeof createProductFilterStore>;

export const initialPageFilterConsidered = clientOnlyRef(false);

export const useProductFilterStore = (): ProductFilterStoreDefinition => {
  const store = inject<ProductFilterStoreDefinition>(
    PRODUCT_FILTER_STORE_INJECT_KEY
  );
  if (!store) {
    throw new Error('no product filter store provided');
  }

  return store;
};

const initialPageParamHandled = clientOnlyRef(false);
export function useInitialPageQueryHandle() {
  return initialPageParamHandled;
}

export function createProductFilterStore(id: string) {
  const store = defineStore(`product-filters-${id}`, () => {
    const route = useRoute();

    const filters = ref<ProductFiltersModel>(
      mergeFilters(DEFAULT_FILTERS, createFiltersFromQuery(route.query))
    );

    const staticFilters = ref<ProductFiltersModel>({});
    // Attribute codes this list needs beyond the product-card defaults the API
    // returns (e.g. the specs configured on a Contentful "Discontinued" list).
    const requestedAttributeCodes = ref<string[]>([]);
    const parentSectionId = ref<string | null>(null);
    const lastResponse = ref<ResponseModel | null>(null);
    const loading = ref<boolean>(false);
    const aggregationsLoading = ref<boolean>(false);
    const error = ref<unknown>(null);
    const abortController = ref<AbortController | null>(null);
    const lastParams = ref<ProductsQueryModel | null>(null);
    // Prevents the route watcher from starting a competing fetch while
    // changePage() already drives the product reload (see ProductSearch.vue).
    const suppressRouteDrivenFetch = ref(false);
    const view = ref<IProductList['fields']['view'] | null>(null);
    // Cumulative Magento facet codes — grows with each aggregations response,
    // never shrinks (zero-result responses omit active facets).
    const knownFilterCodes = ref<Set<string>>(new Set());

    // Initialize currentPage from route query parameter
    const initialPage = (() => {
      const pageFromQuery = route.query[QUERY_KEY_NAME_PAGE];
      if (!initialPageFilterConsidered.value && pageFromQuery) {
        const pageValue = Array.isArray(pageFromQuery)
          ? pageFromQuery[0]
          : pageFromQuery;
        if (pageValue) {
          const pageNumber = parseInt(pageValue, 10);
          if (!isNaN(pageNumber) && pageNumber >= 1) {
            initialPageFilterConsidered.value = true;
            return pageNumber;
          }
        }
      }
      return 1;
    })();

    const currentPage = ref<number>(initialPage);
    const selectedSortOption = ref<string | undefined>(undefined);

    // SKUs recommended by the Lighting Advisor. Kept out of the URL and merged
    // into the Magento query (`sku: { in }`) so the existing loading-skeleton
    // logic applies. A normal filter change supersedes this selection.
    const lightingSkuFilter = ref<string[]>([]);
    // True when the Lighting Advisor was used but returned no recommended
    // products. We then force an empty result (no fallback to the full list).
    const lightingNoMatch = ref<boolean>(false);
    // Page size used to slice the ranked Lighting Advisor SKU list into pages.
    // Mirrors Magento's `grid_per_page`, captured from regular responses.
    const baseLightingPageSize = ref<number>(DEFAULT_PAGE_SIZE);
    let lightingResetHandler: (() => void) | null = null;
    const cameraSkuFilter = ref<string[]>([]);
    const cameraNoMatch = ref<boolean>(false);
    let cameraResetHandler: (() => void) | null = null;
    let filterApplyTimeout: ReturnType<typeof setTimeout> | null = null;

    function cancelScheduledFilterApply() {
      if (filterApplyTimeout !== null) {
        clearTimeout(filterApplyTimeout);
        filterApplyTimeout = null;
      }
    }

    function scheduleFilterApply() {
      cancelScheduledFilterApply();
      filterApplyTimeout = setTimeout(() => {
        filterApplyTimeout = null;
        updateRoute(filters.value);
      }, FILTER_APPLY_DEBOUNCE_MS);
    }

    const _state = {
      filters,
      staticFilters,
      requestedAttributeCodes,
      parentSectionId,
      lastResponse,
      loading,
      aggregationsLoading,
      error,
      abortController,
      lastParams,
      suppressRouteDrivenFetch,
      view,
      knownFilterCodes,
      currentPage,
      selectedSortOption,
      lightingSkuFilter,
      lightingNoMatch,
      baseLightingPageSize,
      cameraSkuFilter,
      cameraNoMatch
    };

    const aggregations = computed<ProductAggregationFragment[]>(
      () => lastResponse.value?.aggregations?.filter(isDefined) ?? []
    );

    // True while the Lighting Advisor holds an active SKU selection (not the
    // no-match sentinel). Drives virtual pagination over the full ranked list.
    const lightingActive = computed(
      () => lightingSkuFilter.value.length > 0 && !lightingNoMatch.value
    );
    const cameraActive = computed(
      () => cameraSkuFilter.value.length > 0 && !cameraNoMatch.value
    );
    const advisorSkuFilter = computed(() =>
      cameraActive.value ? cameraSkuFilter.value : lightingSkuFilter.value
    );
    const advisorActive = computed(
      () => cameraActive.value || lightingActive.value
    );

    const lightingPageSize = computed(
      () => baseLightingPageSize.value || DEFAULT_PAGE_SIZE
    );

    const products = computed<
      ReadonlyArray<CommonProductFragment | CommonProductDetailFragment>
    >(() => {
      const items = lastResponse.value?.items?.filter(isDefined) ?? [];
      if (!advisorActive.value) {
        return items;
      }
      // Magento ignores the order of `sku: { in }`, so restore the
      // recommendation rank for the SKUs returned on the current page.
      const rankBySku = new Map(
        advisorSkuFilter.value.map((sku, index) => [sku, index] as const)
      );
      return [...items].sort(
        (left, right) =>
          (rankBySku.get(left.sku ?? '') ?? Number.MAX_SAFE_INTEGER) -
          (rankBySku.get(right.sku ?? '') ?? Number.MAX_SAFE_INTEGER)
      );
    });

    const hasResults = computed(() => {
      return products.value.length > 0;
    });

    const currencyCode = computed(() => {
      return (
        products.value?.[0]?.price_range.minimum_price?.final_price?.currency ??
        CurrencyEnum.Eur
      );
    });

    const activeFilterCount = computed<number>(() => {
      const known = knownFilterCodes.value;
      return Object.entries(filters.value).filter(([key, values]) => {
        if (
          !Array.isArray(values) ||
          !values.length ||
          EXCLUDED_KEYS.includes(key)
        ) {
          return false;
        }
        // Before first aggregations response, count all non-excluded keys;
        // after, align with pills (known Magento facets + always-allowed).
        return (
          known.size === 0 ||
          known.has(key) ||
          ALWAYS_ALLOWED_FILTER_KEYS.has(key)
        );
      }).length;
    });

    const currentSort = computed(() => {
      return ensureArray(filters.value[QUERY_KEY_NAME_SORT])[0];
    });

    const totalPages = computed(() => {
      if (advisorActive.value) {
        return Math.max(
          1,
          Math.ceil(advisorSkuFilter.value.length / lightingPageSize.value)
        );
      }
      return lastResponse.value?.page_info?.total_pages;
    });
    const pageSize = computed(() =>
      advisorActive.value
        ? lightingPageSize.value
        : lastResponse.value?.page_info?.page_size
    );
    const totalResultCount = computed(() =>
      advisorActive.value
        ? advisorSkuFilter.value.length
        : lastResponse.value?.total_count
    );
    const pageCount = computed(() => totalPages.value);
    const sortOptions = computed(
      () => lastResponse.value?.sort_fields?.options?.filter(isDefined) ?? []
    );
    const defaultSortOption = computed(
      () => lastResponse.value?.sort_fields?.default
    );
    const defaultSortDirection = computed<SortDirection>(() => 'asc');
    const sortDirection = computed<SortDirection>(() => {
      return (
        SORT_DIRECTION_MAP[
          filters.value[QUERY_KEY_NAME_SORT_DIR]?.[0]?.toLowerCase()
        ] ?? defaultSortDirection.value
      );
    });
    const visibleFilters = computed(() => {
      return Object.fromEntries(
        Object.entries(filters.value).filter(
          ([key]) => !EXCLUDED_KEYS.includes(key)
        )
      );
    });

    function parsePageFromQuery(
      pageFromQuery: LocationQuery[typeof QUERY_KEY_NAME_PAGE]
    ): number | null {
      if (!pageFromQuery) {
        return null;
      }

      const pageValue = Array.isArray(pageFromQuery)
        ? pageFromQuery[0]
        : pageFromQuery;
      if (!pageValue) {
        return null;
      }

      const pageNumber = parseInt(pageValue, 10);
      if (isNaN(pageNumber) || pageNumber < 1) {
        return null;
      }

      return pageNumber;
    }

    function resolveTargetPageFromQuery(query: LocationQuery): number {
      const pageFromQuery = query[QUERY_KEY_NAME_PAGE];
      const pageFromUrl = parsePageFromQuery(pageFromQuery);

      if (pageFromUrl !== null) {
        if (!initialPageFilterConsidered.value) {
          initialPageFilterConsidered.value = true;
        }
        return pageFromUrl;
      }

      // No page parameter in the URL always means page 1.
      return 1;
    }

    async function getProducts() {
      const { $shopStore, $locale } = useNuxtApp();

      // In Lighting Advisor mode we paginate the full ranked SKU list
      // client-side and only send the current page's slice to Magento. This
      // keeps the request URL short (avoids HTTP 414) and lets us preserve the
      // recommendation rank across pages.
      const advisorRequest = advisorActive.value;
      const advisorSlice = advisorRequest
        ? advisorSkuFilter.value.slice(
            (currentPage.value - 1) * lightingPageSize.value,
            currentPage.value * lightingPageSize.value
          )
        : null;

      const query: ProductsQueryModel = {
        store: $shopStore.value!,
        locale: $locale.value!,
        filters: mergeFilters(
          DEFAULT_FILTERS,
          filters.value,
          staticFilters.value,
          {
            // The sliced SKUs already represent the requested page, so Magento
            // must return page 1 of that slice.
            [QUERY_KEY_NAME_PAGE]: ensureArray(
              advisorRequest ? '1' : String(currentPage.value)
            )
          }
        ),
        ...(requestedAttributeCodes.value.length
          ? { attributeCodes: requestedAttributeCodes.value }
          : {}),
        ...(lightingNoMatch.value || cameraNoMatch.value
          ? {
              skus: [
                cameraNoMatch.value
                  ? CAMERA_NO_MATCH_SKU
                  : LIGHTING_NO_MATCH_SKU
              ]
            }
          : advisorSlice
            ? {
                skus: advisorSlice,
                pageSize: Math.max(1, advisorSlice.length)
              }
            : {})
      };

      if (JSON.stringify(query) === JSON.stringify(lastParams.value)) {
        return;
      }

      lastParams.value = query;

      if (
        abortController.value &&
        !abortController.value.signal.aborted &&
        loading.value
      ) {
        abortController.value.abort();
      }

      const currentAbortController = new AbortController();
      abortController.value = currentAbortController;

      loading.value = true;
      aggregationsLoading.value = true;
      error.value = null;

      try {
        const productsResponse = await $fetch<ResponseModel>(
          '/api/magento/products',
          {
            ...DEFAULT_FETCH_OPTIONS,
            query,
            signal: currentAbortController.signal
          }
        );

        schedulePrefetchMagentoProductListAttributeMetadata(
          $shopStore.value!,
          $locale.value!,
          productsResponse?.items?.map((item) => item?.sku) ?? []
        );

        // remove category uid filter if category id filter is present.
        // @see https://gcp.baslerweb.com/jira/browse/WEB2-2004
        const categoryId = productsResponse?.aggregations?.find(
          (a) => a?.attribute_code === CATEGORY_ID
        );
        const categoryUid = productsResponse?.aggregations?.find(
          (a) => a?.attribute_code === 'category_uid'
        );

        if (productsResponse?.aggregations && categoryId && categoryUid) {
          productsResponse.aggregations = productsResponse.aggregations.filter(
            (a) => a?.attribute_code !== 'category_uid'
          );
        }

        lastResponse.value = productsResponse;
        // Seed from active filters so zero-result responses (which omit the
        // selected facet) don't drop deeplinked filters on first load.
        knownFilterCodes.value = mergeAggregationCodes(
          knownFilterCodes.value,
          productsResponse?.aggregations?.filter(isDefined) ?? [],
          Object.keys(filters.value)
        );
        filters.value = pruneFiltersToKnownCodes(
          filters.value,
          knownFilterCodes.value
        );
        loading.value = false;
        aggregationsLoading.value = false;

        if (!advisorRequest && productsResponse?.page_info?.page_size) {
          baseLightingPageSize.value = productsResponse.page_info.page_size;
        }

        if (!currentSort.value && productsResponse?.sort_fields?.default) {
          filters.value[QUERY_KEY_NAME_SORT] = [
            productsResponse.sort_fields.default
          ];
        }

        selectedSortOption.value = `${currentSort.value}__${sortDirection.value}`;
      } catch (e) {
        if (currentAbortController.signal.aborted) {
          const loadedPage = lastResponse.value?.page_info?.current_page;
          if (loadedPage !== currentPage.value) {
            lastParams.value = null;
          }
          loading.value = false;
          aggregationsLoading.value = false;
          return;
        }
        error.value = e;
        loading.value = false;
        aggregationsLoading.value = false;
        lastParams.value = null;
      }
    }

    function resetFilters() {
      cancelScheduledFilterApply();
      filters.value = {};
      currentPage.value = 1;
      pushAnalyticsEventOnFilterReset();
      updateRoute(filters.value);
    }

    function toggleSortDirection(direction?: SortDirection) {
      if (direction) {
        handleFormValueChange(QUERY_KEY_NAME_SORT_DIR, direction);
      } else {
        handleFormValueChange(
          QUERY_KEY_NAME_SORT_DIR,
          sortDirection.value === 'asc' ? 'desc' : 'asc'
        );
      }
    }

    function changeSort(sort: string) {
      handleFormValueChange(QUERY_KEY_NAME_SORT, sort);
    }

    function changePage(page: number) {
      if (page < 1) {
        return Promise.resolve();
      }

      // this is needed NOT one level higher, because we want to changePage BEFORE first load!
      if (totalPages.value && page > totalPages.value) {
        return Promise.resolve();
      }

      suppressRouteDrivenFetch.value = true;
      currentPage.value = page;
      handleFormValueChange(QUERY_KEY_NAME_PAGE, currentPage.value.toString());

      return getProducts().finally(() => {
        suppressRouteDrivenFetch.value = false;
      });
    }

    function changeView(newView: NonNullable<IProductList['fields']['view']>) {
      view.value = newView;
    }

    /**
     * Updates the given filter key with the new value.
     * This will automatically push the filters to the url
     * and fetches products based on the new filters.
     *
     * @param key The key to update
     * @param value The new value.
     */
    function handleFormValueChange(key: string, value: string | string[]) {
      // If a non-pagination filter is changing, reset to page 1
      if (!EXCLUDED_KEYS.includes(key)) {
        currentPage.value = 1;
        // A regular product filter supersedes a Lighting Advisor selection.
        // The route change below triggers a reload, so the handler only needs
        // to clear lighting state (without an extra fetch).
        if (lightingResetHandler) {
          lightingResetHandler();
        }
        if (cameraResetHandler) {
          cameraResetHandler();
        }
      }

      filters.value = {
        ...filters.value,
        [key]: ensureArray(value)
      };
      pushAnalyticsEventOnFilterChange(key, value);

      if (EXCLUDED_KEYS.includes(key)) {
        cancelScheduledFilterApply();
        updateRoute(filters.value);
      } else {
        scheduleFilterApply();
      }
    }

    /**
     * Registers a callback invoked when a regular product filter changes, so
     * the Lighting Advisor can reset its selection. Pass `null` to unregister.
     */
    function registerLightingReset(handler: (() => void) | null) {
      lightingResetHandler = handler;
    }

    function registerCameraReset(handler: (() => void) | null) {
      cameraResetHandler = handler;
    }

    /** Applies the Lighting Advisor recommended SKUs and reloads the list. */
    function setLightingSkuFilter(skus: string[]) {
      lightingSkuFilter.value = [...new Set(skus)];
      lightingNoMatch.value = false;
      currentPage.value = 1;
      return getProducts();
    }

    /**
     * Forces an empty product list because the Lighting Advisor was used but
     * returned no recommended products (no fallback to the unfiltered list).
     */
    function setLightingNoMatch() {
      lightingSkuFilter.value = [];
      lightingNoMatch.value = true;
      currentPage.value = 1;
      return getProducts();
    }

    /** Clears the Lighting Advisor SKU filter; reloads unless `reload` is false. */
    function clearLightingSkuFilter(options?: { reload?: boolean }) {
      if (lightingSkuFilter.value.length === 0 && !lightingNoMatch.value) {
        return;
      }
      lightingSkuFilter.value = [];
      lightingNoMatch.value = false;
      if (options?.reload !== false) {
        return getProducts();
      }
    }

    function setCameraSkuFilter(skus: string[]) {
      cameraSkuFilter.value = [...new Set(skus)];
      cameraNoMatch.value = false;
      currentPage.value = 1;
      return getProducts();
    }

    function setCameraNoMatch() {
      cameraSkuFilter.value = [];
      cameraNoMatch.value = true;
      currentPage.value = 1;
      return getProducts();
    }

    function clearCameraSkuFilter(options?: { reload?: boolean }) {
      if (cameraSkuFilter.value.length === 0 && !cameraNoMatch.value) {
        return;
      }
      cameraSkuFilter.value = [];
      cameraNoMatch.value = false;
      if (options?.reload !== false) {
        return getProducts();
      }
    }

    function createFiltersFromRoute(query: LocationQuery) {
      filters.value = pruneFiltersToKnownCodes(
        createFiltersFromQuery(query),
        knownFilterCodes.value
      );
    }

    function createFiltersFromRouteAndUpdate(query: LocationQuery) {
      // Synchronize currentPage with page query parameter FIRST
      const targetPage = resolveTargetPageFromQuery(query);

      // Only update currentPage if it's actually changing
      if (currentPage.value !== targetPage) {
        currentPage.value = targetPage;
      }

      // Update filters from the route query
      const updatedFilters = createFiltersFromQuery(query);

      // Ensure the page parameter in filters matches currentPage to keep state consistent
      if (currentPage.value > 1) {
        updatedFilters[QUERY_KEY_NAME_PAGE] = [String(currentPage.value)];
      } else {
        // Page 1 doesn't need to be in filters
        delete updatedFilters[QUERY_KEY_NAME_PAGE];
      }

      // Drop tracking/junk query keys once Magento facet codes are known.
      filters.value = pruneFiltersToKnownCodes(
        updatedFilters,
        knownFilterCodes.value
      );

      if (suppressRouteDrivenFetch.value) {
        return Promise.resolve();
      }

      return getProducts();
    }

    /**
     * Updates the route based on the given filters.
     *
     * @param filters The filters to use.
     */
    function updateRoute(filters: ProductFiltersModel) {
      const router = useRouter();
      const newParams = createUrlParams(filters);

      // Check if we're already on this route to avoid redundant navigation
      const isSameQuery =
        JSON.stringify(route.query) === JSON.stringify(newParams.query);
      const isSameHash = route.hash === newParams.hash;

      if (!isSameQuery || !isSameHash) {
        // Use nextTick to ensure all reactive updates are complete before navigation
        nextTick(() => {
          router.push(newParams);
        });
      }
    }

    /**
     * Creates relevant url parameters based on the given filters for usage
     * in e.g. {@link useRouter} push method.
     * Default filters are omitted.
     *
     * @param filters The filters to use. Uses current filters by default.
     */
    function createUrlParams(
      newFilters?: ProductFiltersModel
    ): RouteQueryAndHash {
      const clonedFilters = _cloneFilters(newFilters ?? filters.value);

      if (currentSort.value === defaultSortOption.value) {
        delete clonedFilters[QUERY_KEY_NAME_SORT];
      }

      if (sortDirection.value === defaultSortDirection.value) {
        delete clonedFilters[QUERY_KEY_NAME_SORT_DIR];
      }

      // Ensure page parameter matches currentPage.value (source of truth for pagination)
      if (currentPage.value > 1) {
        clonedFilters[QUERY_KEY_NAME_PAGE] = [String(currentPage.value)];
      } else {
        // Page 1 is default, remove from URL for cleanliness
        delete clonedFilters[QUERY_KEY_NAME_PAGE];
      }

      for (const routeOnlyKey of ROUTE_ONLY_QUERY_KEYS) {
        delete clonedFilters[routeOnlyKey];
      }

      const preservedRouteQuery = Object.fromEntries(
        Object.entries(route.query).filter(([key]) =>
          ROUTE_ONLY_QUERY_KEYS.has(key)
        )
      );

      // Preserve the current route hash if it's already set, otherwise use parentSectionId
      // This prevents the hash from changing unexpectedly during navigation
      const currentHash = route.hash;
      const hash =
        currentHash && currentHash.length > 0
          ? currentHash
          : `#${parentSectionId.value}`;

      return {
        query: { ...preservedRouteQuery, ...clonedFilters },
        hash
      };
    }

    /**
     * Creates url params (using {@link createUrlParams}) while removing
     * the given value from the given filter key.
     *
     * @param key The filter key to use.
     * @param valueToRemove The value to remove from this key
     */
    function createNewUrlParamsFromFilter(
      key: string,
      valueToRemove: ReadonlyArray<string>
    ) {
      const clonedFilters = _cloneFilters();
      const values = filters.value[key];
      const arrayValues = ensureArray(values).filter(isDefined).map(String);

      clonedFilters[key] = arrayValues.filter(
        (value) => value && !valueToRemove.includes(value)
      );

      return createUrlParams(clonedFilters);
    }

    function pushAnalyticsEventOnFilterReset() {
      const { $analytics } = useNuxtApp();

      $analytics.pushToDataLayer({
        action: 'reset_filter_selection',
        category: 'filter_selection',
        event: 'click_Internal',
        label: 'Reset'
      });
    }

    function pushAnalyticsEventOnFilterChange(
      key: string,
      value: string | string[]
    ) {
      const { $analytics } = useNuxtApp();

      const label = lastResponse.value?.aggregations?.find(
        (aggregation) => aggregation?.attribute_code === key
      )?.label;

      $analytics.pushToDataLayer({
        event: 'click_Internal',
        category: 'filter_selection',
        action: label,
        label: ensureArray(value).join(',')
      });
    }

    /**
     * Creates a (deep) copy of the given filters or current filters.
     *
     * @param newFilters The filters to use. Uses current filters by default.
     * @internal
     */
    function _cloneFilters(
      newFilters?: ProductFiltersModel
    ): ProductFiltersModel {
      return JSON.parse(JSON.stringify(newFilters ?? filters.value));
    }

    function hydrate() {
      const storedView = readStoredProductListView();
      if (storedView) {
        view.value = storedView;
      }
    }

    return withReset({
      _state,
      filters,
      staticFilters,
      requestedAttributeCodes,
      parentSectionId,
      lastResponse,
      loading,
      aggregationsLoading,
      error,
      abortController,
      lastParams,
      view,
      knownFilterCodes,
      currentPage,
      selectedSortOption,
      lightingSkuFilter,
      lightingNoMatch,
      baseLightingPageSize,
      cameraSkuFilter,
      cameraNoMatch,
      products,
      aggregations,
      hasResults,
      currencyCode,
      activeFilterCount,
      currentSort,
      totalPages,
      pageSize,
      totalResultCount,
      pageCount,
      sortOptions,
      defaultSortOption,
      defaultSortDirection,
      sortDirection,
      visibleFilters,
      handleFormValueChange,
      registerLightingReset,
      registerCameraReset,
      setLightingSkuFilter,
      setLightingNoMatch,
      clearLightingSkuFilter,
      setCameraSkuFilter,
      setCameraNoMatch,
      clearCameraSkuFilter,
      getProducts,
      resetFilters,
      toggleSortDirection,
      changeSort,
      changePage,
      changeView,
      updateRoute,
      createFiltersFromRoute,
      createFiltersFromRouteAndUpdate,
      createNewUrlParamsFromFilter,
      pushAnalyticsEventOnFilterReset,
      pushAnalyticsEventOnFilterChange,
      hydrate
    });
  });

  if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
  }

  return store();
}
